import { useState } from 'react';
import { isAxiosError } from 'axios';
import type { LinkExportCorrectionView, LinkStatusView } from '@reform-lite/contracts';
import { useAuthStore, type AuthUser } from '@/auth/auth-store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useApproveExportCorrection, useReauthenticate } from '@/hooks/use-export-correction';
import { extractApiError } from '@/lib/api-error';
import { useT } from '@/i18n/use-t';

/**
 * VL-400 — correct-and-requeue a Link receipt export refused for an invalid
 * add method. The action is offered only as a hint (the Link advertises the
 * capability, the receipt is failed, the user is a tenant administrator with
 * the explicit grant); the server re-checks every condition. The till's
 * record is never changed; only what is sent to the store system.
 */
export function canApproveCorrections(user: AuthUser | null): boolean {
  // The explicit grant, not the super-admin "holds everything" shortcut (AU6).
  return (
    user?.isSuperAdmin === true && (user.permissions ?? []).includes('connectors.export.repair')
  );
}

type Row = { id: string; kind: string; status: string; reason: string | null };

export function CorrectionAction({
  locationId,
  row,
  view,
}: {
  locationId: string;
  row: Row;
  view: LinkStatusView;
}): JSX.Element | null {
  const t = useT('admin_ui');
  const user = useAuthStore((s) => s.user);
  const approve = useApproveExportCorrection();
  const reauth = useReauthenticate();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(0);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [message, setMessage] = useState<{ tone: 'error' | 'ok'; text: string } | null>(null);

  const corrections = view.corrections ?? [];
  const waiting = corrections.some(
    (c) => c.exportId === row.id && c.lifecycle.stop === null && c.outcome.state === 'none',
  );
  if (
    view.correctionSupported !== true ||
    view.exportOwner !== 'link' ||
    row.kind !== 'receipt' ||
    row.status !== 'failed' ||
    !/addMethod/.test(row.reason ?? '') ||
    waiting ||
    !canApproveCorrections(user)
  ) {
    return null;
  }

  async function submit(): Promise<void> {
    setMessage(null);
    let approvalSent = false;
    try {
      if (needsPassword) {
        await reauth.mutateAsync(password);
        setPassword('');
        setNeedsPassword(false);
      }
      approvalSent = true;
      await approve.mutateAsync({ locationId, exportId: row.id, value, reason });
      setMessage({ tone: 'ok', text: t('settings.integrations.exportStatus.correction.approved') });
      setOpen(false);
    } catch (cause) {
      const code = isAxiosError(cause)
        ? (cause.response?.data as { code?: string } | undefined)?.code
        : undefined;
      if (code === 'reauth_required') {
        setNeedsPassword(true);
        setMessage({
          tone: 'error',
          text: t('settings.integrations.exportStatus.correction.password'),
        });
      } else if (code === 'reauth_unavailable') {
        setMessage({
          tone: 'error',
          text: t('settings.integrations.exportStatus.correction.reauthUnavailable'),
        });
      } else if (
        code === 'approval_outcome_unknown' ||
        (approvalSent && isAxiosError(cause) && cause.response === undefined)
      ) {
        setMessage({
          tone: 'error',
          text: t('settings.integrations.exportStatus.correction.outcomeUnknown'),
        });
      } else {
        setMessage({
          tone: 'error',
          text: `${t('settings.integrations.exportStatus.correction.error')} ${extractApiError(cause)}`,
        });
      }
    }
  }

  return (
    <div className="mt-1 space-y-1 text-left">
      {!open ? (
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          {t('settings.integrations.exportStatus.correction.action')}
        </Button>
      ) : (
        <div className="space-y-2 rounded-md border border-border p-2">
          <div className="flex items-center gap-2">
            <Label htmlFor={`correction-value-${row.id}`}>
              {t('settings.integrations.exportStatus.correction.value')}
            </Label>
            <Select
              id={`correction-value-${row.id}`}
              value={String(value)}
              onChange={(event) => setValue(Number(event.target.value))}
            >
              {[0, 1, 2].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </div>
          <Label htmlFor={`correction-reason-${row.id}`}>
            {t('settings.integrations.exportStatus.correction.reason')}
          </Label>
          <textarea
            id={`correction-reason-${row.id}`}
            className="w-full rounded-md border border-border bg-background p-1 text-xs"
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {needsPassword && (
            <>
              <Label htmlFor={`correction-password-${row.id}`}>
                {t('settings.integrations.exportStatus.correction.password')}
              </Label>
              <input
                id={`correction-password-${row.id}`}
                type="password"
                autoComplete="current-password"
                className="w-full rounded-md border border-border bg-background p-1 text-xs"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              disabled={approve.isPending || reauth.isPending || reason.trim().length < 10}
              onClick={() => void submit()}
            >
              {needsPassword
                ? t('settings.integrations.exportStatus.correction.confirm')
                : t('settings.integrations.exportStatus.correction.submit')}
            </Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('settings.integrations.exportStatus.correction.cancel')}
            </Button>
          </div>
        </div>
      )}
      {message !== null && (
        <p
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={message.tone === 'error' ? 'text-xs text-destructive' : 'text-xs'}
        >
          {message.text}
        </p>
      )}
    </div>
  );
}

const CUSTODY_REASON_KEY: Record<string, string> = {
  never_observed: 'settings.integrations.exportStatus.correction.custody.never_observed',
  inconsistent: 'settings.integrations.exportStatus.correction.custody.inconsistent',
  not_orderable: 'settings.integrations.exportStatus.correction.custody.not_orderable',
  clock_behind: 'settings.integrations.exportStatus.correction.custody.clock_behind',
  aged_out: 'settings.integrations.exportStatus.correction.custody.aged_out',
};

/**
 * The four independent facets of each request (design §10): request
 * lifecycle, Link outcome, export history, current custody. No single status:
 * "handoff attempted" never claims the Link applied it, and a current
 * `failed` is never shown as a loss of the correction.
 */
export function CorrectionList({
  corrections,
}: {
  corrections: readonly LinkExportCorrectionView[];
}): JSX.Element | null {
  const t = useT('admin_ui');
  if (corrections.length === 0) return null;
  return (
    <section
      aria-label={t('settings.integrations.exportStatus.correction.title')}
      className="space-y-1"
    >
      <h5 className="text-xs font-medium">
        {t('settings.integrations.exportStatus.correction.title')}
      </h5>
      <p className="max-w-3xl text-xs text-muted-foreground">
        {t('settings.integrations.exportStatus.correction.description')}
      </p>
      <Table>
        <THead>
          <TR>
            <TH>{t('settings.integrations.exportStatus.correction.document')}</TH>
            <TH>{t('settings.integrations.exportStatus.correction.lifecycle')}</TH>
            <TH>{t('settings.integrations.exportStatus.correction.outcome')}</TH>
            <TH>{t('settings.integrations.exportStatus.correction.history')}</TH>
            <TH>{t('settings.integrations.exportStatus.correction.current')}</TH>
          </TR>
        </THead>
        <TBody>
          {corrections.map((c) => (
            <TR key={c.requestId}>
              <TD>
                <div className="font-mono text-xs">{c.documentId}</div>
                <div className="text-xs text-muted-foreground">
                  {c.approverName} · {c.approvedAt}
                </div>
              </TD>
              <TD className="text-xs">
                <div>{t('settings.integrations.exportStatus.correction.lifecycle.approved')}</div>
                {c.lifecycle.handoffAttempts > 0 && (
                  <div>
                    {t('settings.integrations.exportStatus.correction.lifecycle.attempted')} (
                    {c.lifecycle.handoffAttempts})
                  </div>
                )}
                {c.lifecycle.stop !== null && (
                  <div>
                    {t(
                      c.lifecycle.stop.kind === 'withdrawn_before_handoff'
                        ? c.outcome.conflicts > 0
                          ? 'settings.integrations.exportStatus.correction.lifecycle.withdrawalConflict'
                          : 'settings.integrations.exportStatus.correction.lifecycle.withdrawn'
                        : 'settings.integrations.exportStatus.correction.lifecycle.stopped',
                    )}{' '}
                    <span className="font-mono">{c.lifecycle.stop.code}</span>
                  </div>
                )}
                {c.lifecycle.blockedBy !== null && (
                  <div className="text-muted-foreground">
                    {t('settings.integrations.exportStatus.correction.lifecycle.blocked')}{' '}
                    <span className="font-mono">{c.lifecycle.blockedBy}</span>
                  </div>
                )}
              </TD>
              <TD className="text-xs">
                <Badge variant={c.outcome.state === 'applied' ? 'success' : 'outline'}>
                  {t(`settings.integrations.exportStatus.correction.outcome.${c.outcome.state}`)}
                </Badge>
                {c.outcome.outcome !== null && c.outcome.state === 'refused' && (
                  <span className="ml-1 font-mono">{c.outcome.outcome}</span>
                )}
                {c.outcome.positions.map((p) => (
                  <div key={p.line}>
                    {t('settings.integrations.exportStatus.correction.line')} {p.line}:{' '}
                    {t('settings.integrations.exportStatus.correction.tillSent')}{' '}
                    <span className="font-mono">{JSON.stringify(p.old)}</span>{' '}
                    {t('settings.integrations.exportStatus.correction.meaningUnknown')};{' '}
                    {t('settings.integrations.exportStatus.correction.chose')} {p.new}
                  </div>
                ))}
                {c.outcome.conflicts > 0 && (
                  <div className="text-destructive">
                    {t('settings.integrations.exportStatus.correction.outcome.conflict')} (
                    {c.outcome.conflicts})
                  </div>
                )}
              </TD>
              <TD className="text-xs">
                {c.history.length === 0
                  ? '—'
                  : c.history.map((h) => (
                      <div key={h.writtenAt}>
                        {t('settings.integrations.exportStatus.correction.writtenAt')} {h.writtenAt}
                      </div>
                    ))}
              </TD>
              <TD className="text-xs">
                {c.custody.state === 'unknown' ? (
                  <div>
                    {t('settings.integrations.exportStatus.correction.custody.unknown')}
                    {c.custody.reason !== null && (
                      <span className="text-muted-foreground">
                        {' '}
                        ({t(CUSTODY_REASON_KEY[c.custody.reason] ?? c.custody.reason)})
                      </span>
                    )}
                  </div>
                ) : (
                  <div>
                    {t(`settings.integrations.exportStatus.correction.status.${c.custody.state}`)} —{' '}
                    {t('settings.integrations.exportStatus.correction.custody.asOf')}{' '}
                    {c.custody.receivedAt}
                  </div>
                )}
                {c.lastAccepted !== null && (
                  <div className="text-muted-foreground">
                    {t('settings.integrations.exportStatus.correction.lastAccepted')}{' '}
                    {t(
                      `settings.integrations.exportStatus.correction.status.${c.lastAccepted.exportStatus}`,
                    )}{' '}
                    · {c.lastAccepted.receivedAt}
                    <div>
                      {t('settings.integrations.exportStatus.correction.reportedAt')}{' '}
                      {c.lastAccepted.observedAt}
                    </div>
                    <div>
                      {t('settings.integrations.exportStatus.correction.ageMs')}{' '}
                      {c.lastAccepted.ageMs} ms
                    </div>
                  </div>
                )}
                <div className="text-muted-foreground">
                  {t('settings.integrations.exportStatus.correction.creationOrderUnknown')}
                </div>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </section>
  );
}
